import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Sparkles,
  LineChart,
  Share2,
  ShieldCheck,
  Coins,
  GitCompareArrows,
  Languages,
  FileText,
  Gauge,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import AppFooter from "@/components/AppFooter";
import { useI18n } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";

/* ------------------------------------------------------------------
   Small illustrative SVG helpers — hand-drawn, single-line strokes
------------------------------------------------------------------ */

function Squiggle({ className = "", stroke = "#0A0A0A" }) {
  return (
    <svg viewBox="0 0 120 20" fill="none" className={className}>
      <path
        d="M2 12 C 18 2, 34 22, 50 12 S 82 2, 98 12 118 22, 118 12"
        stroke={stroke}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArrowDoodle({ className = "", stroke = "#0A0A0A" }) {
  return (
    <svg viewBox="0 0 140 80" fill="none" className={className}>
      <path
        d="M6 12 C 40 18, 70 40, 96 66"
        stroke={stroke}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray="1 6"
      />
      <path
        d="M96 66 L 84 60 M 96 66 L 90 52"
        stroke={stroke}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Star({ className = "", fill = "#FFC53D" }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" className={className}>
      <path
        d="M20 2 L 24 16 L 38 20 L 24 24 L 20 38 L 16 24 L 2 20 L 16 16 Z"
        fill={fill}
      />
    </svg>
  );
}

function BlobSVG({ className = "", fill = "#FFC53D" }) {
  return (
    <svg viewBox="0 0 200 200" className={className}>
      <path
        d="M46,-64.3C58.5,-56.3,66.8,-40.7,71.9,-24.4C77,-8.1,79,8.9,74.2,24.2C69.4,39.5,57.8,53.1,42.9,61.4C28,69.7,9.9,72.7,-8.3,72.1C-26.5,71.5,-44.8,67.4,-56.5,56.3C-68.2,45.2,-73.2,27.1,-74.9,8.7C-76.6,-9.7,-75,-28.4,-65.5,-40.3C-56,-52.2,-38.7,-57.4,-22.7,-63.5C-6.7,-69.6,8,-76.7,22.5,-75.4C37,-74.2,51.3,-64.7,46,-64.3Z"
        transform="translate(100 100)"
        fill={fill}
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */

const FAQS = [
  {
    q: "What is a press release ROI calculator?",
    a: "A press release ROI calculator estimates the potential business return of a press release before it is published. PressImpact combines campaign inputs (budget, target audience, expected media pickup) with industry benchmarks to project reach, media value, incremental revenue, and payback across a conservative, base, and optimistic scenario.",
  },
  {
    q: "How does PressImpact estimate impact?",
    a: "PressImpact uses transparent formulas rather than a black-box AI. Every figure on the report links back to an assumption you set, a Nordic industry benchmark, or a modelled calculation. Each source, date, and licence status is printed alongside the number so any CFO or analyst can audit how the estimate was produced.",
  },
  {
    q: "Is earned media value the same as revenue?",
    a: "No. PressImpact clearly labels earned media value as a modelled equivalent, not verified revenue. Media value tells you what comparable paid coverage would have cost. Revenue projections are calculated separately from your conversion rate, deal size, and gross margin — and are always shown as an estimate, not a guarantee.",
  },
  {
    q: "Which benchmarks does PressImpact use?",
    a: "PressImpact seeds public benchmarks from WordStream, HubSpot, Ahrefs, Statista and Nielsen among others. Nordic-specific values are used where licensed data is available; otherwise a clearly labelled global fallback is applied. Every benchmark row shows origin, date, and whether the source is public, modelled, or awaiting validation.",
  },
  {
    q: "Why are some estimates shown as ranges?",
    a: "Press releases are inherently uncertain. PressImpact flexes the three most sensitive inputs — expected lift, conversion rate, and media pickup — by ±30% to produce a conservative, base, and optimistic scenario. This confidence range replaces false-precision single-point forecasts and helps stakeholders understand where the number could land.",
  },
  {
    q: "How are currency conversions calculated?",
    a: "PressImpact fetches live European Central Bank reference rates for USD, EUR, and NOK once every six hours. Converted values are labelled as approximate, and the ECB rate date is printed on every report. Your report stores the original currency separately, so historical figures never change if today's rate moves.",
  },
  {
    q: "Can I use PressImpact outside the Nordics?",
    a: "Yes. PressImpact is Nordic-built but internationally usable. Non-Nordic regions fall back to European or global benchmark averages with a visible notice. You can override any benchmark with your own verified data at any time to sharpen the estimate for your specific market.",
  },
  {
    q: "What data do I need to calculate press release ROI?",
    a: "The Quick mode needs six inputs: campaign budget, baseline monthly traffic, expected lift percentage, conversion rate, average deal or order value, and timeframe. Everything else — industry CPC, media pickup rate, backlink value — pre-fills from benchmarks. Advanced mode lets you override any of the 20+ underlying assumptions.",
  },
  {
    q: "How does PressImpact handle privacy?",
    a: "PressImpact uses a single first-party cookie to remember your language and currency preference — no tracking pixels, no third-party analytics. Email addresses collected via the report gate are used solely to deliver the report you requested via EU-hosted transactional email. See the privacy policy for retention and deletion instructions.",
  },
];

/* ------------------------------------------------------------------ */

export default function Landing() {
  const { t } = useI18n();
  const { theme } = useTheme();
  const dark = theme === "dark";

  // Theme-driven palette
  const palette = dark
    ? {
        page: "bg-[#0A0A0A] text-slate-100",
        headerBg: "bg-[#0A0A0A]/85 backdrop-blur border-white/10",
        eyebrow: "text-[#FFC53D]",
        h1: "text-slate-100",
        h1Muted: "text-slate-500",
        body: "text-slate-400",
        sectionAlt: "bg-[#0F0F10]",
        border: "border-white/10",
        card: "bg-[#141416] border border-white/10 hover:border-[#FFC53D]/60",
        cardText: "text-slate-100",
        cardBody: "text-slate-400",
        stroke: "#FFC53D",
        strokeMuted: "#3F3F46",
        cardMono: "text-slate-500",
      }
    : {
        page: "bg-[#FFFDF6] text-slate-900",
        headerBg: "bg-[#FFFDF6]/85 backdrop-blur border-slate-200",
        eyebrow: "text-orange-600",
        h1: "text-slate-900",
        h1Muted: "text-slate-400",
        body: "text-slate-600",
        sectionAlt: "bg-white",
        border: "border-slate-200",
        card: "bg-white border border-slate-200 hover:border-slate-900",
        cardText: "text-slate-900",
        cardBody: "text-slate-600",
        stroke: "#0A0A0A",
        strokeMuted: "#CBD5E1",
        cardMono: "text-slate-400",
      };

  return (
    <div className={`min-h-screen ${palette.page} transition-colors`}>
      {/* ─────────────── Header ─────────────── */}
      <SiteHeader cta={{ label: t("nav.start") || "Start", to: "/calculator" }} />

      {/* ─────────────── Hero ─────────────── */}
      <section className="relative overflow-hidden">
        {/* Illustrative decorations */}
        <BlobSVG
          className="absolute -top-32 -right-40 w-[560px] opacity-30"
          fill={dark ? "#FFC53D" : "#FFC53D"}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage: dark
              ? "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.10) 1px, transparent 0)"
              : "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.15) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
        <Star className="absolute top-40 right-[45%] h-6 w-6 opacity-90" />
        <Star className="absolute bottom-52 left-8 h-4 w-4 opacity-70" fill="#EF4444" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
            <p className={`text-xs tracking-[0.32em] uppercase font-semibold ${palette.eyebrow}`}>
              PR Analytics · Nordic-built
            </p>
          </div>

          <h1 className={`font-heading font-light text-[2.25rem] leading-[1.08] sm:text-5xl sm:leading-[1.05] md:text-6xl lg:text-[80px] lg:leading-[1.0] tracking-tight max-w-5xl ${palette.h1}`}>
            Every press release,{" "}
            <span className="relative inline-block">
              <span className="relative z-10">a defensible</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 md:h-4 bg-[#FFC53D]/70 -z-0" aria-hidden="true" />
            </span>{" "}
            business case.
          </h1>

          <div className="mt-6 flex items-start gap-4">
            <Squiggle className="h-4 w-24 md:w-32 mt-2 flex-shrink-0" stroke={palette.stroke} />
            <p className={`text-base sm:text-lg leading-[1.55] max-w-2xl ${palette.body}`}>
              Six numbers in — an audit-ready ROI report out. Reach, media value, revenue and
              payback, with every assumption sourced and every scenario ranged. Share it with your
              CFO in one click.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/calculator">
              <Button
                data-testid="hero-start-btn"
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-7 py-6 text-base font-semibold tracking-wide shadow-[6px_6px_0_0_#FFC53D] hover:shadow-[4px_4px_0_0_#FFC53D] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                Run my ROI estimate
                <ArrowUpRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
            <a
              href="#how"
              data-testid="hero-how-link"
              className={`text-sm ${palette.body} hover:${palette.h1} underline underline-offset-4 decoration-dotted transition-colors`}
            >
              See how it works
            </a>
          </div>

          <p className={`mt-6 text-xs tracking-wide ${palette.body}`}>
            ~2 minutes · sourced benchmarks · shareable read-only link
          </p>

          {/* Signal strip — the flow, at a glance */}
          <div className={`mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 border-t ${palette.border} pt-10`}>
            {[
              ["6 inputs", "Enter in minutes"],
              ["±30%", "Sensitivity built in"],
              ["B2B / B2C", "Auto-adapts to your model"],
              ["1 click", "Share the report"],
            ].map(([big, small], idx) => (
              <div key={big} className="relative">
                <div className={`font-heading text-3xl md:text-4xl font-light tracking-tight ${palette.h1}`}>
                  {big}
                </div>
                <div className={`text-[10px] uppercase tracking-[0.28em] mt-2 font-semibold ${
                  dark ? "text-slate-500" : "text-slate-500"
                }`}>
                  {small}
                </div>
                {idx === 0 && <Star className="h-3 w-3 absolute -top-2 -right-2" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── 01 · The Model — cards ─────────────── */}
      <section id="how" className={`relative border-t ${palette.border} ${palette.sectionAlt}`}>
        <ArrowDoodle
          className="absolute -top-6 left-20 h-24 w-24 hidden md:block"
          stroke={palette.strokeMuted}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="mb-16">
            <p className={`text-xs tracking-[0.32em] uppercase font-semibold mb-3 ${palette.eyebrow}`}>
              01 · The model
            </p>
            <h2 className={`font-heading font-light text-4xl md:text-5xl tracking-tight max-w-3xl ${palette.h1}`}>
              Every number shows its formula.
              <br />
              <span className={palette.h1Muted}>Nothing is a black box.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: LineChart, title: "Explicit benefit engine",
                body: "Incremental traffic, lead value, revenue (margin-adjusted), SEO/backlink, brand equivalent. Hover any figure to reveal the maths.",
                tint: "#FFC53D" },
              { icon: Sparkles, title: "AI executive summary",
                body: "Your numbers become a three-sentence executive narrative with a clear Approve, Revise or Reject verdict — bounded to interpret, never invent figures.",
                tint: "#0055FF" },
              { icon: Share2, title: "Shareable · print-ready",
                body: "Every analysis gets a read-only link. Single-page report view is boardroom-ready and prints cleanly to PDF.",
                tint: "#10B981" },
            ].map((f, i) => (
              <div key={f.title} className="relative">
                {/* Offset shadow */}
                <div
                  className="absolute inset-0 translate-x-2 translate-y-2 border"
                  style={{ borderColor: f.tint }}
                  aria-hidden="true"
                />
                <div className={`relative ${palette.card} p-8 transition-colors`}>
                  <div className="flex items-center justify-between mb-8">
                    <div
                      className="h-11 w-11 grid place-items-center"
                      style={{ background: `${f.tint}22` }}
                    >
                      <f.icon className="h-5 w-5" strokeWidth={1.6} style={{ color: f.tint }} />
                    </div>
                    <span className={`mono text-[10px] tracking-[0.2em] ${palette.cardMono}`}>
                      0{i + 1} / 03
                    </span>
                  </div>
                  <h3 className={`font-heading text-xl font-medium mb-4 ${palette.cardText}`}>{f.title}</h3>
                  <p className={`text-sm leading-relaxed ${palette.cardBody}`}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── 02 · Trust layer — cards ─────────────── */}
      <section className={`relative border-t ${palette.border} ${dark ? "bg-[#0A0A0A]" : "bg-[#FFFDF6]"}`}>
        <BlobSVG className="absolute top-20 -left-40 w-[440px] opacity-20" fill="#0055FF" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="mb-16 flex items-start gap-6">
            <div>
            <p className={`text-xs tracking-[0.32em] uppercase font-semibold mb-3 ${palette.eyebrow}`}>
              02 · Trust layer
            </p>
            <h2 className={`font-heading font-light text-4xl md:text-5xl tracking-tight max-w-3xl ${palette.h1}`}>
              Built to{" "}
              <span className="relative inline-block">
                <span className="relative z-10">defend</span>
                <span className="absolute left-0 right-0 bottom-1 h-2.5 md:h-3 bg-emerald-300/70 -z-0" aria-hidden="true" />
              </span>{" "}
              a number in a boardroom,
              <br />
              <span className={palette.h1Muted}>not just to impress in a demo.</span>
            </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Coins, title: "ECB-backed FX", body: "Live daily rates for USD, EUR and NOK from the European Central Bank. Cached for six hours. Source date printed on every report." },
              { icon: ShieldCheck, title: "Sourced benchmarks", body: "Every input traces back to a public source — WordStream, HubSpot, Ahrefs, Nielsen and more. Origin, date and licence status shown per row." },
              { icon: Gauge, title: "CMO + CFO layers", body: "The top scorecard answers what the marketing team spent, reached and moved. One scroll down, the CFO detail is fully sourced and defensible." },
              { icon: Languages, title: "GDPR-clean, EN + SV", body: "British English and Swedish UI out of the box. A single first-party cookie. Zero tracking pixels. Nordic-first from day one." },
            ].map((f) => (
              <div key={f.title} className={`${palette.card} p-6 transition-colors`}>
                <f.icon className={`h-5 w-5 mb-6 ${dark ? "text-slate-300" : "text-slate-700"}`} strokeWidth={1.5} />
                <h3 className={`font-heading text-base font-medium mb-3 ${palette.cardText}`}>{f.title}</h3>
                <p className={`text-xs leading-relaxed ${palette.cardBody}`}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── 03 · Flow — three steps with connecting doodles ─────────────── */}
      <section className={`relative border-t ${palette.border} ${palette.sectionAlt}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="mb-16">
            <p className={`text-xs tracking-[0.32em] uppercase font-semibold mb-3 ${palette.eyebrow}`}>
              03 · How it flows
            </p>
            <h2 className={`font-heading font-light text-4xl md:text-5xl tracking-tight max-w-3xl ${palette.h1}`}>
              Three steps from spreadsheet to signed-off.
            </h2>
          </div>

          <div className="relative grid md:grid-cols-3 gap-8">
            <ArrowDoodle className="absolute top-16 left-[30%] h-14 w-24 hidden md:block" stroke={palette.strokeMuted} />
            <ArrowDoodle className="absolute top-16 left-[63%] h-14 w-24 hidden md:block" stroke={palette.strokeMuted} />

            {[
              { n: "01", title: "Enter six numbers", body: "Budget, baseline traffic, expected lift, conversion rate, deal size and timeframe. Everything else pre-fills from your industry benchmarks — override anything." },
              { n: "02", title: "Watch the model", body: "ROI, payback and net benefit update live as you type. Ranges, sensitivity and a currency-neutral toggle across USD, EUR and NOK." },
              { n: "03", title: "Send the link", body: "Generate a boardroom-ready report with an AI-written executive summary. Copy the URL, print to PDF, or email it to stakeholders in one click." },
            ].map((s) => (
              <div key={s.n} className={`${palette.card} p-8 transition-colors`}>
                <div className="mono text-5xl font-light text-orange-500 mb-8">{s.n}</div>
                <h3 className={`font-heading text-xl font-medium mb-3 ${palette.cardText}`}>{s.title}</h3>
                <p className={`text-sm leading-relaxed ${palette.cardBody}`}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── FAQ (AI-search & SEO friendly) ─────────────── */}
      <section id="faq" className={`border-t ${palette.border} ${palette.sectionAlt}`}>
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24">
          <p className={`text-xs tracking-[0.32em] uppercase font-semibold mb-3 ${palette.eyebrow}`}>
            FAQ
          </p>
          <h2 className={`font-heading font-light text-3xl md:text-4xl tracking-tight mb-12 ${palette.h1}`}>
            Straight answers, no hedging.
          </h2>
          <div className="space-y-8">
            {FAQS.map((item) => (
              <div key={item.q} className={`border-b ${palette.border} pb-6`}>
                <h3 className={`font-heading text-lg font-medium mb-2 ${palette.cardText}`}>{item.q}</h3>
                <p className={`text-sm leading-[1.6] ${palette.cardBody}`}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Final CTA ─────────────── */}
      <section className={`relative border-t ${palette.border} ${dark ? "bg-[#0A0A0A]" : "bg-[#FFFDF6]"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-28">
          <div className={`relative border ${palette.border} ${dark ? "bg-gradient-to-br from-[#141416] to-[#0F0F10]" : "bg-white"} p-10 md:p-16 overflow-hidden`}>
            <BlobSVG className="absolute -top-24 -right-24 w-[320px] opacity-30" fill="#FFC53D" />
            <Star className="absolute top-8 right-8 h-8 w-8" />
            <Star className="absolute bottom-6 left-6 h-4 w-4" fill="#EF4444" />

            <div className="relative">
              <p className={`text-xs tracking-[0.32em] uppercase font-semibold mb-4 ${palette.eyebrow}`}>
                Ready when you are
              </p>
              <h2 className={`font-heading font-light text-4xl md:text-6xl tracking-tight max-w-4xl leading-[1.02] ${palette.h1}`}>
                One press release.
                <br />
                <span className={palette.h1Muted}>One defensible number.</span>
              </h2>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/calculator">
                  <Button
                    data-testid="cta-start-btn"
                    size="lg"
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-8 py-6 text-base font-semibold shadow-[6px_6px_0_0_#FFC53D] hover:shadow-[4px_4px_0_0_#FFC53D] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    Start a new ROI analysis
                    <ArrowUpRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
                <Link
                  to="/compare"
                  data-testid="cta-compare-link"
                  className={`text-sm underline underline-offset-4 decoration-dotted transition-colors ${palette.body} hover:${palette.h1}`}
                >
                  Or compare two options side-by-side
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── Footer ─────────────── */}
      <AppFooter />
    </div>
  );
}
